import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { cartService } from '@/features/cart/services/cartService';
import { useAuth } from '@/hooks/useAuth';
import type { CartResponse, CartResponseWithRelatedData, CartItemStatus, GuestCheckoutFormData } from '@/features/cart/types';
import { CartStatus, ItemStatusCode } from '@/features/cart/types';

export interface UseCheckoutReturn {
  canProceed: boolean;
  isStatusesLoading: boolean;
  isCheckoutProcessing: boolean;
  isCancelingCheckout: boolean;
  isDownloadingInvoice: boolean;
  showGuestCheckoutForm: boolean;
  showGuestConfirmationModal: boolean;
  guestCheckoutData: GuestCheckoutFormData | null;
  checkoutCooldownUntil: string | null;
  isCooldownActive: boolean;
  handleCheckout: () => Promise<void>;
  handleCancelCheckout: () => Promise<void>;
  /** For cart page: cancel checkout on timer expiration, reload page regardless of outcome */
  handleExpiredCancelCheckout: () => Promise<void>;
  handleDownloadInvoice: () => Promise<void>;
  handleGuestCheckoutSubmit: (data: GuestCheckoutFormData) => Promise<void>;
  closeGuestCheckoutForm: () => void;
  closeGuestConfirmationModal: () => void;
  handleGuestConfirmationSuccess: () => void;
}

export function useCheckout(
  cartId: string | undefined,
  cart: CartResponse | null,
  cartResponseWithSecret: CartResponseWithRelatedData | null,
  timeRemaining: number | null,
  refetchCart: () => Promise<void>,
  itemStatuses: Record<string, CartItemStatus | null>,
  hasConflict: boolean = false
): UseCheckoutReturn {
  const navigate = useNavigate();
  const { isGuest, user } = useAuth();
  const [isCheckoutProcessing, setIsCheckoutProcessing] = useState(false);
  const [isCancelingCheckout, setIsCancelingCheckout] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [showGuestCheckoutForm, setShowGuestCheckoutForm] = useState(false);
  const [showGuestConfirmationModal, setShowGuestConfirmationModal] = useState(false);
  const [guestCheckoutData, setGuestCheckoutData] = useState<GuestCheckoutFormData | null>(null);

  // Check if item statuses are still being calculated
  const isStatusesLoading = useMemo((): boolean => {
    // Only check loading state for ACTIVE carts
    if (!cart || cart.status !== CartStatus.Active) {
      return false;
    }

    // If no items, not loading
    if (!cart.items || cart.items.length === 0) {
      return false;
    }

    // Check if any item status is still null (being calculated)
    return cart.items.some(item => itemStatuses[item.id] === null);
  }, [cart, itemStatuses]);

  // Check cooldown status
  const checkoutCooldownUntil = user?.checkout_cooldown_until || null;
  const isCooldownActive = useMemo((): boolean => {
    if (!checkoutCooldownUntil) return false;

    const cooldownDate = new Date(checkoutCooldownUntil);
    const now = new Date();

    // Cooldown is active if the timestamp is in the future
    return cooldownDate > now;
  }, [checkoutCooldownUntil]);

  // Check if cart can proceed to payment based on status
  const canProceed = useMemo((): boolean => {
    if (!cart) return false;

    // If there are conflicts, cannot proceed
    if (hasConflict) {
      return false;
    }

    // Check if cart status allows proceeding to payment
    const allowedStatuses: CartStatus[] = [CartStatus.Active, CartStatus.PendingPayment, CartStatus.FailedPayment];
    if (!allowedStatuses.includes(cart.status)) {
      return false;
    }

    // If cart is PENDING_PAYMENT, check if timer has expired
    if (cart.status === CartStatus.PendingPayment) {
      if (timeRemaining !== null && timeRemaining <= 0) {
        return false;
      }
    }

    // For ACTIVE carts, check cooldown and item statuses
    if (cart.status === CartStatus.Active) {
      // Check if user is in cooldown period
      if (isCooldownActive) {
        return false;
      }

      if (!cart.items || cart.items.length === 0) return false;

      // Check if all statuses are loaded (no null values)
      const allStatusesLoaded = cart.items.every(item => itemStatuses[item.id] !== null);
      if (!allStatusesLoaded) return false;

      // Check if all items are good for booking
      const allGoodForBooking = cart.items.every(item =>
        itemStatuses[item.id]?.item_status_code === ItemStatusCode.GoodForBooking
      );
      return allGoodForBooking;
    }

    // For PENDING_PAYMENT (with valid timer) and FAILED_PAYMENT, allow proceeding
    return true;
  }, [cart, timeRemaining, itemStatuses, hasConflict, isCooldownActive]);

  // Handle checkout - redirect to payment page or show guest form
  const handleCheckout = async () => {
    if (!cartId || !cart) return;

    // If user is a guest, show the guest checkout form
    if (isGuest) {
      setShowGuestCheckoutForm(true);
      return;
    }

    setIsCheckoutProcessing(true);
    try {
      // Check if cart already has client_secret
      if (cartResponseWithSecret?.client_secret) {
        // Redirect directly to payment page
        navigate(`/carts/${cartId}/payment`);
        setIsCheckoutProcessing(false);
        return;
      }

      // If no client_secret, create payment intent via checkout
      await cartService.checkoutCart(cartId);
      // Redirect to payment page (PaymentPage will handle fetching the client_secret)
      navigate(`/carts/${cartId}/payment`);
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('Failed to initiate checkout. Please try again.');
    } finally {
      setIsCheckoutProcessing(false);
    }
  };

  // Handle guest checkout form submission
  const handleGuestCheckoutSubmit = async (data: GuestCheckoutFormData) => {
    if (!cartId) return;

    // Close the form modal and show confirmation modal
    setShowGuestCheckoutForm(false);
    setGuestCheckoutData(data);
    setShowGuestConfirmationModal(true);
  };

  // Close guest checkout form
  const closeGuestCheckoutForm = () => {
    setShowGuestCheckoutForm(false);
  };

  // Close guest confirmation modal
  const closeGuestConfirmationModal = () => {
    setShowGuestConfirmationModal(false);
    setGuestCheckoutData(null);
  };

  // Handle successful guest confirmation (after API call)
  const handleGuestConfirmationSuccess = () => {
    // Redirect to payment page
    if (cartId) {
      navigate(`/carts/${cartId}/payment`);
    }
  };

  // Handle cancel checkout
  const handleCancelCheckout = async () => {
    if (!cartId) return;

    setIsCancelingCheckout(true);
    try {
      await cartService.cancelCheckout(cartId);
      // Refetch the cart to ensure we have the latest data with all items
      await refetchCart();
    } catch (error) {
      console.error('Error canceling checkout:', error);
      toast.error('Failed to cancel checkout. Please try again.');
    } finally {
      setIsCancelingCheckout(false);
    }
  };

  // Handle cancel checkout on timer expiration (cart page)
  // Reloads page regardless of success or failure
  const handleExpiredCancelCheckout = async () => {
    if (!cartId) return;

    setIsCancelingCheckout(true);
    try {
      await cartService.cancelCheckout(cartId);
      // On success, reload page
      window.location.reload();
    } catch (error) {
      console.error('Error canceling expired checkout:', error);
      // On failure, still reload page
      window.location.reload();
    }
    // Note: No finally block since page will reload
  };

  // Handle download invoice
  const handleDownloadInvoice = async () => {
    if (!cartId) return;

    setIsDownloadingInvoice(true);
    try {
      // Fetch the signed download URL from the API
      const response = await cartService.downloadInvoice(cartId);

      if (!response.download_url) {
        throw new Error('Download URL not found in response');
      }

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = `invoice-${cartId}.pdf`; // Suggest a filename
      link.target = '_nightsyncio.invoice.pdf'; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();

      // Clean up the anchor element
      document.body.removeChild(link);

      toast.success('Invoice download started');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return {
    canProceed,
    isStatusesLoading,
    isCheckoutProcessing,
    isCancelingCheckout,
    isDownloadingInvoice,
    showGuestCheckoutForm,
    showGuestConfirmationModal,
    guestCheckoutData,
    checkoutCooldownUntil,
    isCooldownActive,
    handleCheckout,
    handleCancelCheckout,
    handleExpiredCancelCheckout,
    handleDownloadInvoice,
    handleGuestCheckoutSubmit,
    closeGuestCheckoutForm,
    closeGuestConfirmationModal,
    handleGuestConfirmationSuccess,
  };
}

